/**
 * PlanView — "Your audit year."
 *
 * Reads the COA Audit & Monitoring Work Plan (the year's scheduled audits,
 * each carrying its CFR and chapter references) and lets the user start the
 * audit in one click. The regs travel with it: they become the audit's
 * controlling authorities, so the engagement letter and the copilot are
 * grounded from the first second. Plain words, no jargon walls.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { STAGE_LABELS } from './labels.js';

const API = process.env.REACT_APP_API_URL;

const QUARTERS = [['Q1', 'January – March'], ['Q2', 'April – June'], ['Q3', 'July – September'], ['Q4', 'October – December'], ['Q9', 'Anytime / recurring']];
const TYPE_STYLE = {
  Auditing: 'bg-orange-50 text-orange-700 border-orange-200',
  Monitoring: 'bg-sky-50 text-sky-700 border-sky-200',
  Investigations: 'bg-violet-50 text-violet-700 border-violet-200',
};

function regChips(activity) {
  const chips = [];
  String(activity.cfr_references || '').split(/[;\n]+/).map(s => s.trim()).filter(Boolean).forEach(r => chips.push({ kind: 'CFR', name: r }));
  String(activity.chapter_references || '').split(/[;\n]+/).map(s => s.trim()).filter(Boolean).forEach(r => chips.push({ kind: 'Chapter', name: r }));
  return chips;
}

function ActivityCard({ a, onStart, onOpen, onHide, onRestore, starting }) {
  const chips = regChips(a);
  const started = !!a.audit_id;
  const done = a.status === 'completed' || a.audit_stage === 'closed';
  return (
    <div className={`bg-white border rounded-xl px-4 py-3 ${a.hidden ? 'opacity-70 border-dashed border-slate-300' : done ? 'border-emerald-200' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{a.component}</span>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${TYPE_STYLE[a.activity_type] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>{a.activity_type}</span>
            {a.source_app && a.source_app !== 'coa' && <span className="text-[10px] text-slate-400 uppercase">{a.source_app === 'cil' ? 'Issues log' : a.source_app === 'risk' ? 'Risk assessment' : a.source_app}</span>}
            {a.entity_name && <span className="text-[11px] text-slate-500">{a.entity_name}</span>}
            {a.activity_frequency && <span className="text-[10px] text-slate-400">{a.activity_frequency}</span>}
          </div>
          {a.activity_description && <div className="text-[12px] text-slate-500 mt-1 line-clamp-2">{a.activity_description}</div>}
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {chips.slice(0, 6).map((c, i) => (
                <span key={i} className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5"><span className="text-slate-400">{c.kind}</span> {c.name}</span>
              ))}
              {chips.length > 6 && <span className="text-[10px] text-slate-400">+{chips.length - 6} more</span>}
            </div>
          )}
        </div>
        <div className="flex-shrink-0 text-right">
          {a.hidden ? (
            <button onClick={() => onRestore(a.id)} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">Put back on the plan</button>
          ) : done ? (
            <div className="space-y-1">
              <span className="inline-block text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Done{a.number_of_deficiencies > 0 ? ` · ${a.number_of_deficiencies} finding${a.number_of_deficiencies === 1 ? '' : 's'}` : ''}</span>
              {started && <div><button onClick={() => onOpen(a.audit_id)} className="text-[11px] text-orange-600 hover:underline">View audit</button></div>}
            </div>
          ) : started ? (
            <button onClick={() => onOpen(a.audit_id)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-800 text-white shadow">Continue →</button>
          ) : (
            <div className="space-y-1">
              <button onClick={() => onStart(a.id)} disabled={starting === a.id} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">
                {starting === a.id ? 'Setting up…' : 'Start audit'}
              </button>
              <div><button onClick={() => onHide(a.id)} className="text-[10px] text-slate-400 hover:text-slate-600">Hide from this view</button></div>
            </div>
          )}
          {started && !done && !a.hidden && <div className="text-[10px] text-slate-400 mt-1">{STAGE_LABELS[a.audit_stage] || a.audit_stage}</div>}
          {a.hidden && a.hidden_reason && <div className="text-[10px] text-slate-400 mt-1 max-w-[180px]">“{a.hidden_reason}”</div>}
        </div>
      </div>
    </div>
  );
}

export default function PlanView({ onOpenAudit }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [error, setError] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);

  const load = useCallback(async (y, includeHidden = false) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/audit/plan?year=${y}${includeHidden ? '&include_hidden=1' : ''}`, { credentials: 'include' }).then(r => r.json());
      if (r.success) { setActivities(r.activities || []); setYears(r.years?.length ? r.years : [y]); setHiddenCount(r.hidden_count || 0); }
    } catch { /* keep whatever we had */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(year, showHidden); }, [year, showHidden, load]);

  const start = async (activityId) => {
    setStarting(activityId); setError('');
    try {
      const r = await fetch(`${API}/api/audit/plan/${activityId}/start`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' }).then(r => r.json());
      if (!r.success) throw new Error(r.message || 'Could not start the audit');
      onOpenAudit(r.audit_id);
    } catch (e) { setError(e.message); }
    setStarting(null);
  };

  // Hiding is documented and reversible; the work-plan record itself is
  // never touched (it stays in the COA/CPE universe).
  const hide = async (activityId) => {
    const reason = window.prompt('Optional: why hide this from the audit year? (kept in the activity log)') ?? '';
    try {
      const r = await fetch(`${API}/api/audit/plan/${activityId}/dismiss`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) }).then(r => r.json());
      if (!r.success) throw new Error(r.message || 'Could not hide the item');
      await load(year, showHidden);
    } catch (e) { setError(e.message); }
  };
  const restore = async (activityId) => {
    try {
      const r = await fetch(`${API}/api/audit/plan/${activityId}/restore`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' }).then(r => r.json());
      if (!r.success) throw new Error(r.message || 'Could not restore the item');
      await load(year, showHidden);
    } catch (e) { setError(e.message); }
  };

  if (loading) return <div className="text-sm text-slate-400 py-6">Loading your audit year…</div>;

  const byQuarter = QUARTERS.map(([q, label]) => [q, label, activities.filter(a => (a.activity_quarter || 'Q9') === q)]).filter(([, , list]) => list.length);
  const doneCount = activities.filter(a => a.status === 'completed').length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-base font-bold text-slate-800">Your audit year</div>
          <div className="text-[12px] text-slate-400">
            {activities.length
              ? <>Everything scheduled on the {year} work plan, with its regulations attached. {doneCount} of {activities.length} completed.</>
              : <>Nothing scheduled for {year} yet.</>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hiddenCount > 0 && (
            <button onClick={() => setShowHidden(s => !s)} className="text-[11px] font-semibold text-slate-500 hover:text-slate-700">
              {showHidden ? 'Hide the hidden items' : `Show hidden (${hiddenCount})`}
            </button>
          )}
          {years.length > 1 && (
            <select value={year} onChange={e => setYear(Number(e.target.value))} className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 text-slate-700">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
        </div>
      </div>

      {error && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      {!activities.length && (
        <div className="text-sm text-slate-400 bg-slate-50 border border-dashed border-slate-200 rounded-xl px-4 py-8 text-center">
          The annual work plan lives in the COA app. Once activities are planned there, they show up here ready to run — regs included. You can still start a one-off audit from the "All audits" tab.
        </div>
      )}

      {byQuarter.map(([q, label, list]) => (
        <div key={q}>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">{label}</div>
          <div className="space-y-2">
            {list.map(a => <ActivityCard key={a.id} a={a} onStart={start} onOpen={onOpenAudit} onHide={hide} onRestore={restore} starting={starting} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
